import { useCallback } from 'react';
import { LocationFeatureFlagSettingName } from '@weave/schema-gen-ts/dist/schemas/payments/messages/location_settings/location_settings.pb';
import { useQueryClient } from 'react-query';
import { GetMultiLocationFeatureSettingsIO } from '../v1/types';

const MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX = ['ViewV1', 'GetMultiLocationFeatureSettings'] as const;

type MultiLocationFeatureSettings = NonNullable<GetMultiLocationFeatureSettingsIO['output']['settings']>;
type SingleLocationFeatureSettings = MultiLocationFeatureSettings[number];
type UpdatableSingleLocationFeatureSettingKey = Exclude<keyof SingleLocationFeatureSettings, 'locationId'>;

type ResolvedSettingKey<
  T extends
    | UpdatableSingleLocationFeatureSettingKey
    | keyof EnumToSettingKeyMap
    | LocationFeatureFlagSettingName.UNSPECIFIED
> = T extends keyof EnumToSettingKeyMap ? EnumToSettingKeyMap[T] : T & UpdatableSingleLocationFeatureSettingKey;

const ENUM_TO_SETTING_KEY_MAP = {
  [LocationFeatureFlagSettingName.EAGLESOFT_WRITEBACKS_ENABLED]: 'eaglesoftWritebacksEnabled',
  [LocationFeatureFlagSettingName.OPEN_DENTAL_WRITEBACKS_ENABLED]: 'openDentalWritebacksEnabled',
  [LocationFeatureFlagSettingName.DR_CHRONO_WRITEBACKS_ENABLED]: 'drChronoWritebacksEnabled',
  [LocationFeatureFlagSettingName.TTP_AUTH_ENABLED]: 'ttpAuthEnabled',
  [LocationFeatureFlagSettingName.FUSE_WRITEBACKS_ENABLED]: 'fuseWritebacksEnabled',
  [LocationFeatureFlagSettingName.ONLINE_BILL_PAY_FEATURE_ENABLED]: 'onlineBillPayEnabled',
  [LocationFeatureFlagSettingName.OPEN_DENTAL_CLOUD_WRITEBACKS_ENABLED]: 'openDentalCloudWritebacksEnabled',
  [LocationFeatureFlagSettingName.DENTRIX_ENTERPRISE_WRITEBACKS_ENABLED]: 'dentrixEnterpriseWritebacksEnabled',
  [LocationFeatureFlagSettingName.DOLPHIN_CLOUD_WRITEBACKS_ENABLED]: 'dolphinCloudWritebacksEnabled',
  [LocationFeatureFlagSettingName.OPEN_DENTAL_LOCAL_WRITEBACKS_ENABLED]: 'openDentalLocalWritebacksEnabled',
  [LocationFeatureFlagSettingName.AUTO_RECEIPT_ENABLED]: 'autoReceiptEnabled',
  [LocationFeatureFlagSettingName.SURCHARGING_ENABLED]: 'surchargingEnabled',
  [LocationFeatureFlagSettingName.ONLINE_BILL_PAY_PATIENT_BIRTHDATE_ENABLED]: 'onlineBillPayPatientBirthdateEnabled',
  [LocationFeatureFlagSettingName.ONLINE_BILL_PAY_PATIENT_IDENTIFIER_ENABLED]: 'onlineBillPayPatientIdentifierEnabled',
  [LocationFeatureFlagSettingName.DOLPHIN_WRITEBACKS_ENABLED]: 'dolphinWritebacksEnabled',
  [LocationFeatureFlagSettingName.CRYSTAL_WRITEBACKS_ENABLED]: 'crystalWritebacksEnabled',
  [LocationFeatureFlagSettingName.NEXTECH_SELECT_WRITEBACKS_ENABLED]: 'nextechSelectWritebacksEnabled',
  [LocationFeatureFlagSettingName.NEXTECH_PRACTICE_WRITEBACKS_ENABLED]: 'nextechPracticeWritebacksEnabled',
  [LocationFeatureFlagSettingName.ATHENA_ONE_WRITEBACKS_ENABLED]: 'athenaOneWritebacksEnabled',
  [LocationFeatureFlagSettingName.DOLPHIN_LEGACY_WRITEBACKS_ENABLED]: 'dolphinLegacyWritebacksEnabled',
} as const;

type EnumToSettingKeyMap = typeof ENUM_TO_SETTING_KEY_MAP;

const isMultiLocationFeatureSettingsQueryKey = (
  queryKey: unknown
): queryKey is readonly [
  (typeof MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX)[0],
  (typeof MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX)[1],
  GetMultiLocationFeatureSettingsIO['input']
] => {
  if (!Array.isArray(queryKey) || queryKey.length < 3) {
    return false;
  }

  return (
    queryKey[0] === MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX[0] &&
    queryKey[1] === MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX[1] &&
    typeof queryKey[2] === 'object' &&
    queryKey[2] !== null
  );
};

export const useUpdateMultiLocationFeatureSettingsCache = () => {
  const queryClient = useQueryClient();

  const updateMultiLocationFeatureSettingsCache = useCallback(
    <
      Input extends
        | UpdatableSingleLocationFeatureSettingKey
        | keyof EnumToSettingKeyMap
        | LocationFeatureFlagSettingName.UNSPECIFIED
    >(
      locationId: string,
      settingKey: Input,
      value: SingleLocationFeatureSettings[ResolvedSettingKey<Input>]
    ) => {
      const resolvedKey = (
        settingKey in ENUM_TO_SETTING_KEY_MAP
          ? ENUM_TO_SETTING_KEY_MAP[settingKey as keyof EnumToSettingKeyMap]
          : settingKey
      ) as UpdatableSingleLocationFeatureSettingKey;

      const matchingQueries = queryClient
        .getQueryCache()
        .findAll(MULTI_LOCATION_FEATURE_SETTINGS_QUERY_KEY_PREFIX)
        .filter(({ queryKey }) => {
          if (!isMultiLocationFeatureSettingsQueryKey(queryKey)) {
            return false;
          }

          return queryKey[2].locationIds?.includes(locationId) ?? false;
        });

      matchingQueries.forEach(({ queryKey }) => {
        queryClient.setQueryData<GetMultiLocationFeatureSettingsIO['output'] | undefined>(queryKey, (cachedData) => {
          if (!cachedData?.settings?.length) {
            return cachedData;
          }

          let hasUpdatedLocation = false;
          const nextSettings = cachedData.settings.map((locationSettings) => {
            if (locationSettings.locationId !== locationId) {
              return locationSettings;
            }

            hasUpdatedLocation = true;
            return {
              ...locationSettings,
              [resolvedKey]: value,
            };
          });

          if (!hasUpdatedLocation) {
            return cachedData;
          }

          return {
            ...cachedData,
            settings: nextSettings,
          };
        });
      });
    },
    [queryClient]
  );

  return updateMultiLocationFeatureSettingsCache;
};
